"use client";

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { VendorInvitationStatusBadge } from '@/components/vendor/VendorInvitationStatusBadge';
import { PlatformVendorInvitation } from '@/lib/types/vendorInvitation';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { format } from 'date-fns';
import { Send, Clock, CheckCircle, TrendingUp, Users } from 'lucide-react';

interface PlatformStats {
  totalInvitations: number;
  acceptanceRate: number;
  activeInviters: number;
  avgDaysToAccept: number;
}

interface TopInviter {
  user_email: string;
  org_name: string;
  total_sent: number;
  accepted_count: number;
  acceptance_rate: number;
}

interface InvitationWithRelations {
  id: string;
  invited_email: string;
  invited_by_user_id: string;
  inviter_organization_id: string;
  status: string;
  personal_message: string | null;
  created_at: string;
  accepted_at: string | null;
  expires_at: string;
  inviter_email?: string;
  organization_name?: string;
}

export default function AdminVendorInvitationsPage() {
  const [invitations, setInvitations] = useState<InvitationWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [platformStats, setPlatformStats] = useState<PlatformStats>({
    totalInvitations: 0,
    acceptanceRate: 0,
    activeInviters: 0,
    avgDaysToAccept: 0,
  });
  const [topInviters, setTopInviters] = useState<TopInviter[]>([]);
  const { toast } = useToast();

  const fetchAllData = async () => {
    try {
      setLoading(true);

      // Fetch all invitations
      const { data: invitationsData, error: invError } = await supabase
        .from('platform_vendor_invitations')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (invError) throw invError;

      // Fetch user emails and org names separately
      const userIds = [...new Set(invitationsData?.map(i => i.invited_by_user_id) || [])];
      const orgIds = [...new Set(invitationsData?.map(i => i.inviter_organization_id) || [])];

      // Get user emails via secure Edge Function (admin-only)
      const userEmailMap = new Map<string, string>();
      if (userIds.length > 0) {
        const { data: emailData, error: emailError } = await invokeEdgeFunction('get-user-emails', {
          body: { userIds },
          callerOperation: 'VendorInvitations.fetchEmails',
        });

        const emailResult = emailData as { emails?: Record<string, string> };
        if (!emailError && emailResult?.emails) {
          Object.entries(emailResult.emails).forEach(([id, email]) => {
            userEmailMap.set(id, email as string);
          });
        }
      }

      // Get org names
      const { data: orgs } = await supabase
        .from('organizations')
        .select('id, name')
        .in('id', orgIds);
      const orgNameMap = new Map<string, string>();
      orgs?.forEach(o => {
        if (o.id && o.name) {
          orgNameMap.set(o.id, o.name);
        }
      });

      // Enrich invitation data
      const enrichedInvitations: InvitationWithRelations[] = invitationsData?.map(inv => ({
        ...inv,
        inviter_email: userEmailMap.get(inv.invited_by_user_id),
        organization_name: orgNameMap.get(inv.inviter_organization_id),
      })) || [];

      setInvitations(enrichedInvitations);

      // Calculate platform stats
      const total = invitationsData?.length || 0;
      const accepted = invitationsData?.filter(i => i.status === 'accepted').length || 0;
      const acceptanceRate = total > 0 ? Math.round((accepted / total) * 100) : 0;

      // Calculate unique active inviters (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const activeInviters = new Set(
        invitationsData
          ?.filter(i => new Date(i.created_at) >= thirtyDaysAgo)
          .map(i => i.invited_by_user_id)
      ).size;

      // Calculate avg days to accept
      const acceptedInvitations = invitationsData?.filter(i => i.accepted_at) || [];
      let avgDays = 0;
      if (acceptedInvitations.length > 0) {
        const totalDays = acceptedInvitations.reduce((sum, inv) => {
          const created = new Date(inv.created_at);
          const accepted = new Date(inv.accepted_at!);
          const days = Math.floor((accepted.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0);
        avgDays = Math.round(totalDays / acceptedInvitations.length);
      }

      setPlatformStats({
        totalInvitations: total,
        acceptanceRate,
        activeInviters,
        avgDaysToAccept: avgDays,
      });

      // Calculate top inviters
      const inviterMap = new Map<string, { sent: number; accepted: number }>();
      
      invitationsData?.forEach(inv => {
        const userId = inv.invited_by_user_id;
        
        if (!inviterMap.has(userId)) {
          inviterMap.set(userId, { sent: 0, accepted: 0 });
        }
        
        const stats = inviterMap.get(userId)!;
        stats.sent++;
        if (inv.status === 'accepted') {
          stats.accepted++;
        }
      });

      const topInvitersData: TopInviter[] = Array.from(inviterMap.entries())
        .map(([userId, stats]) => {
          const invitation = enrichedInvitations.find(i => i.invited_by_user_id === userId);
          return {
            user_email: invitation?.inviter_email || userId.substring(0, 12) + '...',
            org_name: invitation?.organization_name || 'Unknown',
            total_sent: stats.sent,
            accepted_count: stats.accepted,
            acceptance_rate: stats.sent > 0 ? Math.round((stats.accepted / stats.sent) * 100) : 0,
          };
        })
        .sort((a, b) => b.total_sent - a.total_sent)
        .slice(0, 10);

      setTopInviters(topInvitersData);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch invitation data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Vendor Invitations</h1>
        <p className="text-muted-foreground mt-1">
          Platform-wide vendor invitation analytics and activity
        </p>
      </div>

      {/* Platform Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Invitations</p>
                <p className="text-2xl font-bold text-foreground">{platformStats.totalInvitations}</p>
              </div>
              <Send className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Acceptance Rate</p>
                <p className="text-2xl font-bold text-foreground">{platformStats.acceptanceRate}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Avg Days to Accept</p>
                <p className="text-2xl font-bold text-foreground">{platformStats.avgDaysToAccept}</p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Inviters</p>
                <p className="text-2xl font-bold text-foreground">{platformStats.activeInviters}</p>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Inviters */}
      <Card>
        <CardHeader>
          <CardTitle>Top Inviters</CardTitle>
          <CardDescription>Users who have sent the most invitations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead className="text-center">Total Sent</TableHead>
                  <TableHead className="text-center">Accepted</TableHead>
                  <TableHead className="text-center">Acceptance Rate</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {topInviters.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  topInviters.map((inviter, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{inviter.user_email}</TableCell>
                      <TableCell>{inviter.org_name}</TableCell>
                      <TableCell className="text-center">{inviter.total_sent}</TableCell>
                      <TableCell className="text-center">{inviter.accepted_count}</TableCell>
                      <TableCell className="text-center">
                        <span className={`font-medium ${inviter.acceptance_rate >= 30 ? 'text-green-600' : 'text-muted-foreground'}`}>
                          {inviter.acceptance_rate}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Invitations</CardTitle>
          <CardDescription>Latest vendor invitations across all organizations</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invited Email</TableHead>
                  <TableHead>Invited By</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent Date</TableHead>
                  <TableHead>Accepted Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invitations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No invitations found
                    </TableCell>
                  </TableRow>
                ) : (
                  invitations.slice(0, 50).map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell className="font-medium">{invitation.invited_email}</TableCell>
                      <TableCell>
                        <div className="text-sm">{invitation.inviter_email || invitation.invited_by_user_id.substring(0, 12) + '...'}</div>
                      </TableCell>
                      <TableCell>{invitation.organization_name || invitation.inviter_organization_id.substring(0, 12) + '...'}</TableCell>
                      <TableCell>
                        <VendorInvitationStatusBadge status={invitation.status as any} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(invitation.created_at), 'MMM d, yyyy')}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {invitation.accepted_at
                          ? format(new Date(invitation.accepted_at), 'MMM d, yyyy')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
